import React from 'react';
import { TimeFrameValue } from '@givelify/utils';
import { Skeleton } from '@mui/material';
import { DonationsStatisticType } from 'api/client/endpoints';
import { BasicErrorBoundary } from 'components';
import { useOverviewLoaderData } from 'pages/overview/hooks';
import { WidgetPaper, WidgetErrorBox } from '../../components';
import { EmptyState } from '../components/EmptyState';
import { TrendData } from '../types';
import { BarChart } from './components/BarChart';
import { ChartHeader } from './components/ChartHeader';
import { BarChartWrapper } from './styles';

export type OverviewTrendChartViewComponentProps = {
    isLoading: boolean;
    data: TrendData;
    showEmptyState: boolean;
    totalDonation: number;
    donationsCount: number;
    rangeDateType: DonationsStatisticType;
    timeFrame: TimeFrameValue;
    dataKey: string;
};

const OverviewTrendChartViewComponent: React.FCC<
    OverviewTrendChartViewComponentProps
> = ({
    isLoading,
    data,
    showEmptyState,
    totalDonation,
    donationsCount,
    rangeDateType,
    timeFrame,
    dataKey,
}) => {
    const { navigatedFromOtherPage } = useOverviewLoaderData();

    return (
        <WidgetPaper>
            <BasicErrorBoundary
                fallback={<WidgetErrorBox height="100%" width="100%" />}
            >
                {isLoading ? (
                    navigatedFromOtherPage && (
                        <Skeleton
                            data-testid="trend-chart-skeleton"
                            height="100%"
                            variant="rectangular"
                        />
                    )
                ) : (
                    <>
                        <ChartHeader
                            dateEnd={timeFrame.end}
                            dateStart={timeFrame.start}
                            donationsCount={donationsCount}
                            total={totalDonation}
                        />

                        <BarChartWrapper>
                            {showEmptyState ? (
                                <EmptyState
                                    endDate={timeFrame.end}
                                    startDate={timeFrame.start}
                                />
                            ) : null}
                            <BarChart
                                data={data}
                                dataKey={dataKey}
                                rangeDateType={rangeDateType}
                            />
                        </BarChartWrapper>
                    </>
                )}
            </BasicErrorBoundary>
        </WidgetPaper>
    );
};

export const OverviewTrendChartView = React.memo(
    OverviewTrendChartViewComponent,
);
